/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import BookingAction, {PackageUpsell, PaymentAssert, ReservationNote} from "store/booking/BookingAction";
import {Activity} from "types/entities/Activity";
import update from "immutability-helper";
import {Package} from "types/entities/Package";
import {BookingFilter, BookingProcess, BookingTimeslot} from "types/app";
import {Company} from "types/entities/Company";
import {PackagesCart} from "types/entities/PackagesCart";
import {Cookies} from "types/entities/Cookies";
import {Cookies_status} from "types/enums/Cookies_status";
import {Payment} from "types/entities/Payment";
import {Timeslot} from "types/entities/Timeslot";
import {PackageCategory} from "types/enums/PackageCategory";
import {PackagesItemByPerson, PackagesItemsResponse} from "types/entities/PackagesItems";
import {RouteName} from "utils/hooks/useNavigation";

export interface BookingReducerSetActivitiesAction {
    payload: Activity[];
    type: typeof BookingAction.SET_ACTIVITIES;
}

export interface BookingReducerSetPackagesAction {
    payload: Package[];
    type: typeof BookingAction.SET_PACKAGES;
}

export interface BookingReducerSetAvailablePackagesItemsAction {
    payload: PackagesItemsResponse;
    type: typeof BookingAction.SET_AVAILABLE_PACKAGES_ITEMS;
}

export interface BookingReducerSetSelectedPackageItemsAction {
    payload: PackagesItemByPerson[];
    type: typeof BookingAction.SET_SELECTED_PACKAGE_ITEMS;
}

export interface BookingReducerSetSelectedPackageItemAction {
    payload: PackagesItemByPerson;
    type: typeof BookingAction.SET_SELECTED_PACKAGE_ITEM;
}

export interface BookingReducerResetSelectedPackageItemsAction {
    payload: [];
    type: typeof BookingAction.RESET_SELECTED_PACKAGE_ITEMS;
}

export interface BookingReducerSetTimeslotsAction {
    payload: BookingTimeslot[];
    type: typeof BookingAction.SET_TIMESLOTS;
}

export interface BookingReducerSetTimeslotsAllowedAction {
    payload: boolean;
    type: typeof BookingAction.SET_TIMESLOTS_ALLOWED;
}

export interface BookingReducerSetPackageCartAction {
    payload: PackagesCart;
    type: typeof BookingAction.SET_PACKAGE_CART;
}

export interface BookingReducerSetDateChosenByCustomerAction {
    payload: boolean;
    type: typeof BookingAction.SET_DATE_CHOSEN_BY_CUSTOMER;
}

export interface BookingReducerSetEmailVerifiedAction {
    payload: boolean;
    type: typeof BookingAction.SET_EMAIL_VERIFIED;
}

export interface BookingReducerSetFilterAction {
    payload: BookingFilter;
    type: typeof BookingAction.SET_FILTER;
}

export interface BookingReducerSetFilterDrivesAdultCountAction {
    payload: number;
    type: typeof BookingAction.SET_FILTER_ADULTS_COUNT;
}

export interface BookingReducerSetFilterDrivesChildCountAction {
    payload: number;
    type: typeof BookingAction.SET_FILTER_CHILDREN_COUNT;
}

export interface BookingReducerSetPackageAction {
    payload: Package | null;
    type: typeof BookingAction.SET_PACKAGE;
}

export interface BookingReducerPushReservationNoteAction {
    payload: ReservationNote;
    type: typeof BookingAction.PUSH_RESERVATION_NOTE;
}

export interface BookingReducerResetReservationNoteAction {
    payload: null | ReservationNote;
    type: typeof BookingAction.RESET_RESERVATION_NOTE;
}

export interface BookingReducerPushUpsellAction {
    payload: PackageUpsell;
    type: typeof BookingAction.PUSH_UPSELL;
}

export interface BookingReducerSetUpsellAction {
    payload: PackageUpsell;
    type: typeof BookingAction.SET_UPSELL;
}

export interface BookingReducerRemoveUpsellAction {
    payload: PackageUpsell;
    type: typeof BookingAction.REMOVE_UPSELL;
}

export interface BookingReducerSetCustomerAction {
    payload: Company;
    type: typeof BookingAction.SET_CUSTOMER;
}

export interface BookingReducerSetCookiesAction {
    payload: Cookies;
    type: typeof BookingAction.SET_COOKIES;
}

export interface BookingReducerSetPackagesCartPaymentAction {
    payload: Payment | null;
    type: typeof BookingAction.SET_PACKAGES_CART_PAYMENT;
}

export interface BookingReducerSetPackagesCartPaymentAssertAction {
    payload: PaymentAssert | null;
    type: typeof BookingAction.SET_PACKAGES_CART_PAYMENT_ASSERT;
}

export interface BookingReducerSetCurrentNavigationStepAction {
    payload: BookingNavigationStep;
    type: typeof BookingAction.SET_CURRENT_NAVIGATION_STEP;
}

export interface BookingReducerClearBookingProcessAction {
    payload: null;
    type: typeof BookingAction.CLEAR_BOOKING_PROCESS_STATE;
}

export interface BookingReducerClearUpsellsAction {
    payload: any[];
    type: typeof BookingAction.CLEAR_UPSELLS;
}

export interface BookingReducerSetPossibleTimeslotsAction {
    payload: Timeslot[];
    type: typeof BookingAction.SET_POSSIBLE_TIMESLOTS;
}

export interface BookingReducerSetCustomTimeslotsAction {
    payload: Timeslot;
    type: typeof BookingAction.SET_CUSTOM_TIMESLOT;
}

export interface BookingReducerSetSelectedTimeslotAction {
    payload: Timeslot;
    type: typeof BookingAction.SET_SELECTED_TIMESLOT;
}

export interface BookingReducerResetCustomTimeslotsAction {
    payload: [];
    type: typeof BookingAction.RESET_CUSTOM_TIMESLOTS;
}

type BookingReducerAction =
    | BookingReducerSetActivitiesAction
    | BookingReducerSetPackagesAction
    | BookingReducerSetTimeslotsAction
    | BookingReducerSetTimeslotsAllowedAction
    | BookingReducerSetPackageCartAction
    | BookingReducerSetEmailVerifiedAction
    | BookingReducerSetDateChosenByCustomerAction
    | BookingReducerSetFilterAction
    | BookingReducerSetFilterDrivesAdultCountAction
    | BookingReducerSetFilterDrivesChildCountAction
    | BookingReducerSetPackageAction
    | BookingReducerPushReservationNoteAction
    | BookingReducerResetReservationNoteAction
    | BookingReducerSetUpsellAction
    | BookingReducerPushUpsellAction
    | BookingReducerRemoveUpsellAction
    | BookingReducerSetCustomerAction
    | BookingReducerSetCookiesAction
    | BookingReducerSetPackagesCartPaymentAction
    | BookingReducerSetPackagesCartPaymentAssertAction
    | BookingReducerSetCurrentNavigationStepAction
    | BookingReducerClearBookingProcessAction
    | BookingReducerSetPossibleTimeslotsAction
    | BookingReducerSetCustomTimeslotsAction
    | BookingReducerResetCustomTimeslotsAction
    | BookingReducerClearUpsellsAction
    | BookingReducerSetSelectedTimeslotAction
    | BookingReducerSetAvailablePackagesItemsAction
    | BookingReducerResetSelectedPackageItemsAction
    | BookingReducerSetSelectedPackageItemAction
    | BookingReducerSetSelectedPackageItemsAction;

export interface BookingNavigation {
    currentStep: BookingNavigationStep;
    timeslotsAllowedSteps: BookingNavigationStep[];
    timeslotsGiftAllowedSteps: BookingNavigationStep[];
    timeslotsNotAllowedSteps: BookingNavigationStep[];
}

export interface BookingNavigationStep {
    id: number;
    label: string;
}

export interface AllowedStep {
    id: number;
    label: string;
    routeName: RouteName;
}

export const TIMESLOTS_ALLOWED_STEPS: AllowedStep[] = [
    {id: 1, label: "Home", routeName: RouteName.Main},
    {id: 2, label: "Options", routeName: RouteName.Options},
    {id: 3, label: "Timeslots", routeName: RouteName.TimeslotsNew},
    {id: 4, label: "Package", routeName: RouteName.Packages},
    {id: 5, label: "Recapitulation", routeName: RouteName.Customer},
    {id: 6, label: "Payment", routeName: RouteName.Paid},
];

export const TIMESLOTS_ALLOWED_STEPS_FOR_GIFT: AllowedStep[] = [
    {id: 1, label: "Home", routeName: RouteName.Main},
    {id: 2, label: "Gift voucher", routeName: RouteName.Gift},
    {id: 3, label: "Checkout", routeName: RouteName.GiftCheckout},
];

export const TIMESLOTS_UPSELLS_ALLOWED_STEPS: AllowedStep[] = [
    {id: 1, label: "Home", routeName: RouteName.Main},
    {id: 2, label: "Options", routeName: RouteName.Options},
    {id: 3, label: "Timeslots", routeName: RouteName.TimeslotsNew},
    {id: 4, label: "Package", routeName: RouteName.Packages},
    {id: 5, label: "Upsells", routeName: RouteName.UpsellsNew},
    {id: 6, label: "Recapitulation", routeName: RouteName.Customer},
    {id: 7, label: "Payment", routeName: RouteName.Paid},
];

const TIMESLOT_NOT_ALLOWED_STEPS = [
    {id: 1, label: "Packages"},
    {id: 2, label: "Customer"},
    {id: 3, label: "Paid"},
];

export interface BookingReducerState {
    activities: Activity[];
    packages: Package[];
    timeslotsAllowed: boolean;
    timeslots: BookingTimeslot[];
    bookingProcess: BookingProcess;
    cookies: Cookies;
    navigation: BookingNavigation;
}

const getStandardDate = () => {
    const date = new Date();

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based, so we add 1
    const day = String(date.getDate()).padStart(2, "0");

    const formattedDate = `${year}-${month}-${day}`;
    return formattedDate;
};
const initBookingProcess: BookingProcess = {
    filter: {
        adults: 0,
        children: 0,
        date: getStandardDate(),
        activity_ids: [],
        groupType: [],
        hours_count: 0,
        category: PackageCategory.General,
    },
    package: null,
    upsell: [],
    customer: null,
    packageCart: null,
    voucher: null,
    payment: null,
    paymentAssert: null,
    notes: [],
    customTimeslots: [],
    possibleTimeslots: [],
    dateChosenByCustomer: false,
    emailVerified: false,
    selectedTimeslot: null,
    availablePackageItems: null,
    selectedPackageItems: [],
};

class BookingReducer {
    initialState: BookingReducerState = {
        activities: [],
        packages: [],
        timeslotsAllowed: true,
        timeslots: [],
        bookingProcess: initBookingProcess,
        cookies: {tech_cookies: true, analytics_cookies: false, advert_cookies: false, status: Cookies_status.NULL},
        navigation: {
            currentStep: TIMESLOTS_ALLOWED_STEPS[0],
            timeslotsAllowedSteps: TIMESLOTS_ALLOWED_STEPS,
            timeslotsGiftAllowedSteps: TIMESLOTS_ALLOWED_STEPS_FOR_GIFT,
            timeslotsNotAllowedSteps: TIMESLOT_NOT_ALLOWED_STEPS,
        },
    };

    reducer = (state: BookingReducerState = this.initialState, action: BookingReducerAction): BookingReducerState => {
        switch (action.type) {
            case BookingAction.SET_DATE_CHOSEN_BY_CUSTOMER:
                return update(state, {
                    bookingProcess: {
                        dateChosenByCustomer: {
                            $set: action.payload,
                        },
                    },
                });

            case BookingAction.SET_EMAIL_VERIFIED:
                return update(state, {
                    bookingProcess: {
                        emailVerified: {
                            $set: action.payload,
                        },
                    },
                });

            case BookingAction.SET_POSSIBLE_TIMESLOTS:
                return update(state, {
                    bookingProcess: {
                        possibleTimeslots: {
                            $set: action.payload,
                        },
                    },
                });
            case BookingAction.SET_CUSTOM_TIMESLOT:
                const timeslotIndex = state.bookingProcess.customTimeslots.findIndex(timeslot => timeslot.id === action.payload.id);
                if (timeslotIndex === -1) {
                    return update(state, {
                        bookingProcess: {
                            customTimeslots: {
                                $push: [action.payload],
                            },
                        },
                    });
                }

                return update(state, {
                    bookingProcess: {
                        customTimeslots: {
                            $splice: [[timeslotIndex, 1]],
                        },
                    },
                });
            case BookingAction.RESET_CUSTOM_TIMESLOTS:
                return update(state, {
                    bookingProcess: {
                        customTimeslots: {
                            $set: [],
                        },
                    },
                });

            case BookingAction.SET_ACTIVITIES:
                return update(state, {
                    activities: {
                        $set: action.payload,
                    },
                });
            case BookingAction.SET_PACKAGES:
                return update(state, {
                    packages: {
                        $set: action.payload,
                    },
                });

            case BookingAction.SET_AVAILABLE_PACKAGES_ITEMS:
                return update(state, {
                    bookingProcess: {
                        availablePackageItems: {
                            $set: action.payload,
                        },
                    },
                });

            case BookingAction.SET_SELECTED_PACKAGE_ITEMS:
                return update(state, {
                    bookingProcess: {
                        selectedPackageItems: {
                            $set: action.payload,
                        },
                    },
                });

            case BookingAction.SET_SELECTED_TIMESLOT:
                return update(state, {
                    bookingProcess: {
                        selectedTimeslot: {
                            $set: action.payload,
                        },
                    },
                });
            case BookingAction.SET_SELECTED_PACKAGE_ITEM:
                const selectedPackageItemIndex = state.bookingProcess.selectedPackageItems.findIndex(item => item.personIndex === action.payload.personIndex);
                if (selectedPackageItemIndex === -1) {
                    return update(state, {
                        bookingProcess: {
                            selectedPackageItems: {
                                $push: [action.payload],
                            },
                        },
                    });
                }

                return update(state, {
                    bookingProcess: {
                        selectedPackageItems: {
                            [selectedPackageItemIndex]: {
                                $set: action.payload,
                            },
                        },
                    },
                });

            case BookingAction.RESET_SELECTED_PACKAGE_ITEMS:
                return update(state, {
                    bookingProcess: {
                        selectedPackageItems: {
                            $set: [],
                        },
                    },
                });

            case BookingAction.SET_TIMESLOTS_ALLOWED:
                return update(state, {
                    timeslotsAllowed: {
                        $set: action.payload,
                    },
                });
            case BookingAction.SET_TIMESLOTS:
                return update(state, {
                    timeslots: {
                        $set: action.payload,
                    },
                });

            case BookingAction.SET_PACKAGE_CART:
                return update(state, {
                    bookingProcess: {
                        packageCart: {
                            $set: action.payload,
                        },
                    },
                });
            case BookingAction.SET_FILTER:
                action.payload.adults = +action.payload.adults;
                action.payload.children = +action.payload.children;
                return update(state, {
                    bookingProcess: {
                        filter: {
                            $set: action.payload,
                        },
                    },
                });

            case BookingAction.SET_FILTER_ADULTS_COUNT:
                action.payload = +action.payload;
                return update(state, {
                    bookingProcess: {
                        filter: {
                            adults: {
                                $set: action.payload,
                            },
                        },
                    },
                });

            case BookingAction.SET_FILTER_CHILDREN_COUNT:
                action.payload = +action.payload;
                return update(state, {
                    bookingProcess: {
                        filter: {
                            children: {
                                $set: action.payload,
                            },
                        },
                    },
                });

            case BookingAction.SET_PACKAGE:
                return update(state, {
                    bookingProcess: {
                        package: {
                            $set: action.payload,
                        },
                    },
                });
            case BookingAction.RESET_RESERVATION_NOTE:
                return update(state, {
                    bookingProcess: {
                        notes: {
                            $set: [...initBookingProcess.notes],
                        },
                    },
                });

            case BookingAction.PUSH_RESERVATION_NOTE:
                const notesIndex = state.bookingProcess.notes.findIndex(note => note.id === action.payload.id);
                if (notesIndex !== -1) {
                    return update(state, {
                        bookingProcess: {
                            notes: {
                                [notesIndex]: {
                                    $set: action.payload,
                                },
                            },
                        },
                    });
                }
                return update(state, {
                    bookingProcess: {
                        notes: {
                            $push: [action.payload],
                        },
                    },
                });

            case BookingAction.SET_UPSELL:
                const upsellFoundIndex = state.bookingProcess.upsell.findIndex(upsell => upsell.packageItem.id === action.payload.packageItem.id);
                if (upsellFoundIndex !== -1) {
                    return update(state, {
                        bookingProcess: {
                            upsell: {
                                [upsellFoundIndex]: {
                                    $set: action.payload,
                                },
                            },
                        },
                    });
                }
                return state;

            case BookingAction.PUSH_UPSELL:
                const upsellIndex = state.bookingProcess.upsell.findIndex(upsell => upsell.packageItem.id === action.payload.packageItem.id);

                if (upsellIndex !== -1) {
                    return update(state, {
                        bookingProcess: {
                            upsell: {
                                [upsellIndex]: {
                                    $set: action.payload,
                                },
                            },
                        },
                    });
                }

                return update(state, {
                    bookingProcess: {
                        upsell: {
                            $push: [action.payload],
                        },
                    },
                });

            case BookingAction.REMOVE_UPSELL:
                const filteredUpsells = state.bookingProcess.upsell?.filter(upsell => upsell.packageItem.id !== action.payload.packageItem.id);

                return update(state, {
                    bookingProcess: {
                        upsell: {
                            $set: filteredUpsells,
                        },
                    },
                });

            case BookingAction.SET_CUSTOMER:
                return update(state, {
                    bookingProcess: {
                        customer: {
                            $set: action.payload,
                        },
                    },
                });

            case BookingAction.SET_COOKIES:
                return update(state, {
                    cookies: {
                        $set: action.payload,
                    },
                });

            case BookingAction.SET_PACKAGES_CART_PAYMENT:
                return update(state, {
                    bookingProcess: {
                        payment: {
                            $set: action.payload,
                        },
                    },
                });

            case BookingAction.SET_PACKAGES_CART_PAYMENT_ASSERT:
                return update(state, {
                    bookingProcess: {
                        paymentAssert: {
                            $set: action.payload,
                        },
                    },
                });

            case BookingAction.SET_CURRENT_NAVIGATION_STEP:
                return update(state, {
                    navigation: {
                        currentStep: {
                            $set: action.payload,
                        },
                    },
                });

            case BookingAction.CLEAR_BOOKING_PROCESS_STATE:
                return update(state, {
                    bookingProcess: {
                        $set: {
                            ...initBookingProcess,
                        },
                    },
                });
            case BookingAction.CLEAR_UPSELLS:
                return update(state, {
                    bookingProcess: {
                        upsell: {
                            $set: [],
                        },
                    },
                });

            default:
                return state;
        }
    };
}

export default BookingReducer;
